import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CspConfig } from 'data/cspConfig/cspConfig.types';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/cspConfig/cspConfig.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplateSection } from 'data/template/template.types';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import CspConfigForm, { CspConfigFormRef } from '../../components/CspConfigForm';
import { cspConfigTemplate } from '../../templates/cspConfigTemplate';
import getCspConfigDataToSubmit from '../../utils/getCspConfigDataToSubmit';
import getContactOptions from '../../utils/getContactOptions';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  contacts: contactSelectors.getContacts(state),
});

const mapDispatch = {
  createCspConfig: actions.createCspConfig,
  goToCspConfigList: actions.goToCspConfigList,
  getContacts: contactActions.getAllContacts,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CspConfigCreate: FunctionComponent<Props> = ({
  deliveryFunctionId,
  history,
  isAuditEnabled,
  userId,
  userName,
  contacts,
  createCspConfig,
  goToCspConfigList,
  getContacts,
}: Props) => {
  const [template, setTemplate] = useState(cspConfigTemplate);
  const [formData, setFormData] = useState<Partial<CspConfig>>({});
  const cspConfigFormRef = useRef<CspConfigFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getContacts(deliveryFunctionId);
  }, [deliveryFunctionId, getContacts]);

  useEffect(() => {
    setFormData({
      name: '',
      cspid: '',
      legalcontact: '',
      nwcontact: '',
    });
  }, []);

  useEffect(() => {
    const clonedTemplate = cloneDeep(cspConfigTemplate);
    clonedTemplate.formId = Date.now().toString();

    (clonedTemplate.main as FormTemplateSection).fields.legalcontact.options = getContactOptions(contacts);
    (clonedTemplate.main as FormTemplateSection).fields.nwcontact.options = getContactOptions(contacts);

    setTemplate(clonedTemplate);
  }, [contacts]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    const isValid = cspConfigFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = cspConfigFormRef?.current?.getValues();
    if (!formValues) {
      return;
    }

    const body = getCspConfigDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createCspConfig(deliveryFunctionId, body, history);
  };

  const handleClickCancel = () => goToCspConfigList(history);

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
          onClickClose={handleClickCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CspConfigCreate">
      {renderFormPanel(t('cspConfig.create.title'))}
      <CspConfigForm
        data={formData}
        template={template}
        ref={cspConfigFormRef}
        onFieldUpdate={handleFieldUpdate}
      />
    </div>
  );
};

export default withRouter(connector(CspConfigCreate));
